import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import Utilities from "../../foundation/Utility";
import FileExplorerEntry, {
    FileExplorerResponse,
    FileExplorerTypes,
} from "../../types/system/FileExplorer";
import File from "../../types/files/File";
import store, { IAppState } from "../../state/Store";
import Utility from "../../foundation/Utility";
import { MockPromiseWrapper } from "../../types/system/MockPromiseWrapper";

// Define the type for our state store entry
type FileExplorersState = {
    open: boolean;
    current: FileExplorerEntry;
    explorers: FileExplorerEntry[];
};

// Define the initial state for our state store entry
const initialState: FileExplorersState = {
    open: false,
    current: null,
    explorers: [],
};

// Create a slice to hold all our reducers
const fileExplorerSlice = createSlice({
    name: "fileExplorer",
    initialState: initialState,
    reducers: {
        setOpen: (state, action: PayloadAction<boolean>) => {
            state.open = action.payload;
        },
        addFileExplorer: (state, action: PayloadAction<FileExplorerEntry>) => {
            state.explorers = [...state.explorers, action.payload];
        },
        moveNextFileExplorer: state => {
            state.current = state.explorers.splice(0, 1)[0];
            state.open = !!state.current;
        },
    },
    extraReducers(builder) {
        builder.addCase(REHYDRATE, (_, rawData: any) => {
            if (rawData?.key !== "tts") return undefined;
            return {
                open: false,
                current: null,
                explorers: [],
            };
        });
    },
});

const {
    setOpen,
    addFileExplorer: addFileExplorerReducer,
    moveNextFileExplorer: moveNextFileExplorerReducer,
} = fileExplorerSlice.actions;

export const addFileExplorer = createAsyncThunk(
    "fileExplorer/addFileExplorer",
    async (payload: FileExplorerEntry, thunkAPI) => {
        const { fileExplorer } = store.getState().db;

        const id = Utility.newGuid().toString();
        payload.type = payload.type ?? FileExplorerTypes.Any;
        payload.promise = new MockPromiseWrapper<FileExplorerResponse>(
            id,
            id,
            () => {},
        );
        thunkAPI.dispatch(addFileExplorerReducer(payload));
        if (!fileExplorer.open) thunkAPI.dispatch(moveNextFileExplorer(null));
        return payload.promise.promise;
    },
);
export const moveNextFileExplorer = createAsyncThunk(
    "fileExplorer/moveNextFileExplorer",
    async (selected: File, thunkAPI) => {
        const { fileExplorer } = store.getState().db;

        let wasOpen = fileExplorer.open;

        thunkAPI.dispatch(setOpen(false));
        if (wasOpen) {
            fileExplorer.current?.callback?.(
                selected,
                selected ? "ok" : "cancel",
            );
            fileExplorer.current?.promise?.resolve({
                selected,
                button: selected ? "ok" : "cancel",
                response: !!selected,
            });
            await Utilities.waitFor(500);
        }
        thunkAPI.dispatch(moveNextFileExplorerReducer());
    },
);

// We have to export the reducer as this is added to the state store
export default fileExplorerSlice.reducer;
